import React, { useRef, useEffect, forwardRef, useImperativeHandle, useMemo } from 'react';
import { Tooltip } from 'antd';
import {
  FolderOutlined,
  FileOutlined,
  CheckSquareOutlined,
  CaretRightOutlined,
  CaretDownOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { useGantt } from '../context/GanttContext';
import { FlattenedTask, ROW_HEIGHT, TaskLevelColors, HEADER_HEIGHT } from '../types/gantt.types';
import { useWbsTreeName } from '../../../pages/ProjectScheduleManagement/shared/useWbsCodes';

export interface TaskListPanelRef {
  scrollTo: (scrollTop: number) => void;
}

interface TaskListPanelProps {
  onScroll?: (scrollTop: number) => void;
}

const TaskListPanel = forwardRef<TaskListPanelRef, TaskListPanelProps>(
  ({ onScroll }, ref) => {
    const { tasks, taskTree, expandedKeys, toggleExpand, criticalTaskIds, showCriticalPath } =
      useGantt();
    // Phase/activity names embed a WBS code; resolve against the full tree so
    // labels don't shift as rows expand. Task names are already clean.
    const treeLabel = useWbsTreeName(taskTree);
    const scrollRef = useRef<HTMLDivElement>(null);

    useImperativeHandle(ref, () => ({
      scrollTo: (scrollTop: number) => {
        if (scrollRef.current) {
          scrollRef.current.scrollTop = scrollTop;
        }
      },
    }));

    // Handle scroll sync
    useEffect(() => {
      const scrollContainer = scrollRef.current;
      if (!scrollContainer || !onScroll) return;

      const handleScroll = () => {
        onScroll(scrollContainer.scrollTop);
      };

      scrollContainer.addEventListener('scroll', handleScroll, { passive: true });
      return () => scrollContainer.removeEventListener('scroll', handleScroll);
    }, [onScroll]);

    // Build a map to track which tasks have children
    const tasksWithChildren = useMemo(() => {
      const set = new Set<string>();
      tasks.forEach(task => {
        if (task.children && task.children.length > 0) {
          set.add(task.key);
        }
      });
      return set;
    }, [tasks]);

    const getLevelIcon = (level: string) => {
      switch (level) {
        case 'phase':
          return <FolderOutlined style={{ color: TaskLevelColors.phase }} />;
        case 'activity':
          return <FileOutlined style={{ color: TaskLevelColors.activity }} />;
        default:
          return <CheckSquareOutlined style={{ color: TaskLevelColors.task }} />;
      }
    };

    return (
      <div
        style={{
          width: 530,
          minWidth: 530,
          borderRight: '1px solid #d9d9d9',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {/* Header - matches TimelineHeader height */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            height: HEADER_HEIGHT,
            background: '#fafafa',
            borderBottom: '1px solid #d9d9d9',
            fontWeight: 600,
            fontSize: 12,
            flexShrink: 0,
          }}
        >
          <div style={{ width: 250, paddingLeft: 12 }}>Task Name</div>
          <div style={{ width: 90, textAlign: 'center' }}>Start</div>
          <div style={{ width: 90, textAlign: 'center' }}>End</div>
          <div style={{ width: 50, textAlign: 'center' }}>Days</div>
          <div style={{ width: 45, textAlign: 'center' }}>%</div>
        </div>

        {/* Scrollable body */}
        <div
          ref={scrollRef}
          style={{
            flex: 1,
            overflowY: 'auto',
            overflowX: 'hidden',
            minHeight: 0,
          }}
        >
          {/* Content container */}
          <div style={{ minHeight: tasks.length * ROW_HEIGHT }}>
            {tasks.map((task) => {
              const hasChildren = tasksWithChildren.has(task.key);
              const isExpanded = expandedKeys.has(task.key);
              const isCritical = showCriticalPath && criticalTaskIds.has(task.id);

              return (
                <div
                  key={task.key}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    height: ROW_HEIGHT,
                    borderBottom: '1px solid #f0f0f0',
                    background: isCritical ? 'rgba(255, 77, 79, 0.05)' : '#fff',
                    fontSize: 12,
                  }}
                  className="gantt-row"
                >
                  {/* Task Name */}
                  <div
                    style={{
                      width: 250,
                      minWidth: 250,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6,
                      paddingLeft: 12 + task.indentLevel * 20,
                      paddingRight: 8,
                      overflow: 'hidden',
                    }}
                  >
                    {hasChildren ? (
                      <span
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleExpand(task.key);
                        }}
                        style={{ cursor: 'pointer', width: 16, flexShrink: 0 }}
                      >
                        {isExpanded ? <CaretDownOutlined /> : <CaretRightOutlined />}
                      </span>
                    ) : (
                      <span style={{ width: 16, flexShrink: 0 }} />
                    )}
                    {getLevelIcon(task.level)}
                    <Tooltip title={task.name}>
                      <span
                        style={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          color: isCritical ? '#ff4d4f' : 'inherit',
                          fontWeight: isCritical ? 600 : 400,
                        }}
                      >
                        {task.level === 'task'
                          ? task.name
                          : treeLabel(task.key, task.name)}
                      </span>
                    </Tooltip>
                  </div>
                  {/* Start Date */}
                  <div style={{ width: 90, textAlign: 'center', flexShrink: 0 }}>
                    {task.startDate ? (
                      (() => {
                        const today = dayjs();
                        const plannedStart = dayjs(task.startDate);
                        const actualStart = task.actualStartDate
                          ? dayjs(task.actualStartDate)
                          : null;
                        const isOnTimeStart =
                          !!actualStart && !actualStart.isAfter(plannedStart, 'day');
                        // Overdue: planned start is today or in the past AND
                        // task hasn't actually started on time (no actual yet,
                        // or actual happened after planned).
                        const isOverdueStart =
                          !isOnTimeStart && !plannedStart.isAfter(today, 'day');
                        const startColor = isOverdueStart
                          ? '#fa8c16'
                          : isOnTimeStart
                          ? '#52c41a'
                          : 'inherit';
                        const tooltip = (
                          <div style={{ fontSize: 12 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                              <span style={{ color: 'rgba(255,255,255,0.65)' }}>Planned Start:</span>
                              <span>{plannedStart.format('DD-MMM-YYYY')}</span>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                              <span style={{ color: 'rgba(255,255,255,0.65)' }}>Actual Start:</span>
                              <span>
                                {actualStart ? actualStart.format('DD-MMM-YYYY') : '--'}
                              </span>
                            </div>
                            {!!actualStart && actualStart.isAfter(plannedStart, 'day') && (
                              <div
                                style={{
                                  marginTop: 4,
                                  paddingTop: 4,
                                  borderTop: '1px solid rgba(255,255,255,0.2)',
                                  color: '#ffa940',
                                }}
                              >
                                Delayed by {actualStart.diff(plannedStart, 'day')} day(s)
                              </div>
                            )}
                          </div>
                        );
                        return (
                          <Tooltip title={tooltip} color="rgba(0,0,0,0.85)">
                            <span
                              style={{
                                color: startColor,
                                fontWeight: isOverdueStart || isOnTimeStart ? 600 : 400,
                                cursor: 'default',
                              }}
                            >
                              {plannedStart.format('DD-MMM-YY')}
                            </span>
                          </Tooltip>
                        );
                      })()
                    ) : (
                      '--'
                    )}
                  </div>
                  {/* End Date */}
                  <div style={{ width: 90, textAlign: 'center', flexShrink: 0 }}>
                    {task.endDate ? (
                      (() => {
                        const today = dayjs();
                        const plannedEnd = dayjs(task.endDate);
                        const actualEnd = task.actualEndDate
                          ? dayjs(task.actualEndDate)
                          : null;
                        const isOnTimeEnd =
                          !!actualEnd && !actualEnd.isAfter(plannedEnd, 'day');
                        const isOverdueEnd =
                          !isOnTimeEnd && !plannedEnd.isAfter(today, 'day');
                        const endColor = isOverdueEnd
                          ? '#fa8c16'
                          : isOnTimeEnd
                          ? '#52c41a'
                          : 'inherit';
                        const tooltip = (
                          <div style={{ fontSize: 12 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                              <span style={{ color: 'rgba(255,255,255,0.65)' }}>Planned End:</span>
                              <span>{plannedEnd.format('DD-MMM-YYYY')}</span>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                              <span style={{ color: 'rgba(255,255,255,0.65)' }}>Actual End:</span>
                              <span>{actualEnd ? actualEnd.format('DD-MMM-YYYY') : '--'}</span>
                            </div>
                            {!!actualEnd && actualEnd.isAfter(plannedEnd, 'day') && (
                              <div
                                style={{
                                  marginTop: 4,
                                  paddingTop: 4,
                                  borderTop: '1px solid rgba(255,255,255,0.2)',
                                  color: '#ffa940',
                                }}
                              >
                                Delayed by {actualEnd.diff(plannedEnd, 'day')} day(s)
                              </div>
                            )}
                          </div>
                        );
                        return (
                          <Tooltip title={tooltip} color="rgba(0,0,0,0.85)">
                            <span
                              style={{
                                color: endColor,
                                fontWeight: isOverdueEnd || isOnTimeEnd ? 600 : 400,
                                cursor: 'default',
                              }}
                            >
                              {plannedEnd.format('DD-MMM-YY')}
                            </span>
                          </Tooltip>
                        );
                      })()
                    ) : (
                      '--'
                    )}
                  </div>
                  {/* Duration */}
                  <div style={{ width: 50, textAlign: 'center', flexShrink: 0 }}>
                    {task.duration || 0}d
                  </div>
                  {/* Progress */}
                  <div style={{ width: 45, textAlign: 'center', flexShrink: 0 }}>
                    {Math.round(task.progress || 0)}%
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <style>{`
          .gantt-row:hover {
            background: rgba(24, 144, 255, 0.05) !important;
          }
        `}</style>
      </div>
    );
  }
);

TaskListPanel.displayName = 'TaskListPanel';

export default TaskListPanel;
